import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { WebSocketSubject } from 'rxjs/webSocket';
import { catchError, retry } from 'rxjs/operators';
import { forkJoin, interval, Subject, timer } from 'rxjs';
import { environment } from 'src/environments/environment';
import { RosterService } from './openapi/roster';
import { MatSnackBar } from '@angular/material/snack-bar';

const LOCALSTORAGE_KEY = 'progress_timetable';

export interface AddTimeTableData {
  fileId: string;
  routeId: string;
  blockIds?: string[];
  filename: string;
  startDay?: string;
  endDay?: string;
  data?: any; // data used to update timetable
  dataType?: TimetableProgressType;
}
export interface TimeTableProgressData {
  id: string;
  route: string;
  filename: string;
  started: boolean;
  progress?: number;
  completed: boolean;
  rosterCreated?: boolean;
  error: boolean;
  rejected: boolean;
  statusMessage?: string;
  subscribed: boolean;
  blockIds?: string[];
  startDay: string;
  endDay: string;
  data?: any;
  dataType?: TimetableProgressType;
}

export type TimetableProgressType =
  | 'TIMETABLE'
  | 'EDIT_TRIP'
  | 'DELETE_TRIP'
  | 'ADD_TRIP';

export type TimetableProgressStatus =
  | 'PROCESSING'
  | 'SUCCESS'
  | 'ERROR'
  | 'DELETE'
  | 'EDIT'
  | 'ROSTER_CREATE'
  | 'ROSTER_DELETE'
  | 'REJECTED'
  | 'UNSUBSCRIBE'
  | 'LAST_STATUS';
export interface TimeTableProgressWebSocketData {
  status: TimetableProgressStatus;
  message: string | TimeTableProgressWebSocketData[];
  fileId: string;
  tripId?: string;
}

@Injectable({
  providedIn: 'root',
})
export class ProgressTimetableService {
  uploadData: TimeTableProgressData[] = [];
  updates$: Subject<TimeTableProgressData> =
    new Subject<TimeTableProgressData>();
  constructor(
    private httpClient: HttpClient,
    private rosterService: RosterService,
    private snackbar: MatSnackBar
  ) {
    // this.loadCurrentProgress();
  }

  get list() {
    return this.uploadData;
  }

  get length() {
    return this.uploadData.length;
  }

  get completed() {
    return this.uploadData.filter(item => item?.completed).length;
  }

  add(data: AddTimeTableData) {
    const {
      fileId: id,
      filename,
      routeId: route,
      startDay,
      endDay,
      blockIds,
      data: inputData,
      dataType = 'TIMETABLE',
    } = data;
    const newItem: TimeTableProgressData = {
      id,
      filename,
      route,
      started: false,
      completed: false,
      error: false,
      rejected: false,
      subscribed: false,
      startDay,
      endDay,
      blockIds,
      data: inputData,
      dataType,
      // completed: false,
    };
    this.uploadData.push(newItem);
    if (!this.socketWs$) {
      this.initWs();
    } else {
      this.sendWs('subscribe', id);
    }
    newItem.started = true;
    // localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(this.uploadData));
  }

  getProgressData(id: string) {
    return this.uploadData.find(data => data?.id === id);
  }

  removeCompleted() {
    this.uploadData = this.uploadData.filter(data => {
      const { completed, error, rejected, subscribed, id } = data;
      if (completed) {
        if (subscribed) {
          this.sendWs('unsubscribe', id);
        }
        return false;
      }
      return true;
    });
  }

  remove(id: string) {
    // console.log('id', id);
    const currData = this.getProgressData(id);
    if (currData && currData.subscribed) {
      this.sendWs('unsubscribe', id);
    }
    this.uploadData = this.uploadData.filter(data => data.id !== id);
    // localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(this.uploadData));
  }

  clear() {
    this.uploadData.forEach(data => {
      if (data?.id) {
        this.sendWs('unsubscribe', data?.id);
      }
    });
    this.uploadData = [];
    // localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify([]));
  }

  socketWs$: WebSocketSubject<any>;
  heartbeat$;
  initWs() {
    this.socketWs$ = new WebSocketSubject({
      url: environment.apiUrl.gtfsWs,
      openObserver: {
        next: value => {
          console.log('Websocket opened: ', value);
          if (this.uploadData.length > 0) {
            console.log(
              `Connected. Attempting to subscribe to  ${this.uploadData.length} upload events.`
            );
            this.uploadData.forEach(data => {
              if (!(data.completed || data.error || data.rejected)) {
                this.sendWs('subscribe', data.id);
              }
            });
          }
        },
      },
      closeObserver: {
        next: value => {
          console.log('Websocket closed: ', value);
        },
      },
    });
    this.socketWs$
      .pipe(
        retry({ delay: () => timer(2000) }),
        catchError(err => {
          console.error(err);
          return err;
        })
      )
      .subscribe({
        next: (response: TimeTableProgressWebSocketData) => {
          // console.log('WS response: ', response);
          const { fileId, status, message, tripId } = response || {};

          if (status === 'LAST_STATUS' && typeof message !== 'string') {
            console.log('Update last status');
            message.forEach(websocketData => {
              const { fileId, status, message, tripId } = websocketData;
              this.processSocketData(fileId, status, message, tripId);
            });
          } else {
            this.processSocketData(fileId, status, message, tripId);
          }
        },
        error: err => {
          console.error('Websocket Error: ', err);
        },
        complete: () => {
          console.log('complete');
        },
      });
    // this.startHeartbeat();
  }

  processSocketData(
    fileId,
    status: TimetableProgressStatus,
    message: string | TimeTableProgressWebSocketData[] = '',
    tripId?: string
  ) {
    let currData = this.getProgressData(fileId);
    if (currData) {
      console.log('Process Data: ', fileId, status, message);
      switch (status) {
        case 'PROCESSING': {
          currData.started = true;
          if (typeof message === 'string') currData.statusMessage = message;
          break;
        }
        case 'ERROR': {
          if (!currData.error) {
            this.sendWs('unsubscribe', fileId);
            if (typeof message === 'string') {
              currData.statusMessage = message;
              this.snackbar.open(`${currData.route}: ${message}`, null, {
                duration: 3000,
                panelClass: 'custom-snack-bar-panel-error',
              });
            }
          }
          currData.error = true;
          currData.subscribed = false;
          break;
        }
        case 'REJECTED': {
          if (!currData.rejected) {
            this.sendWs('unsubscribe', fileId);
            if (typeof message === 'string') {
              currData.statusMessage = message;
              this.snackbar.open(`${currData.route}: ${message}`, null, {
                duration: 3000,
                panelClass: 'custom-snack-bar-panel-error',
              });
            }
          }
          currData.rejected = true;
          currData.subscribed = false;
          break;
        }
        case 'DELETE':
        case 'EDIT':
        case 'SUCCESS':
        case 'ROSTER_DELETE':
        case 'ROSTER_CREATE': {
          if (!currData.completed) {
            this.sendWs('unsubscribe', fileId);
            if (typeof message === 'string') currData.statusMessage = message;
          }
          currData.completed = true;
          currData.subscribed = false;

          if (status === 'EDIT') {
            this.updates$.next(currData);
          }

          if (status === 'SUCCESS' && typeof tripId === 'string') {
            currData.filename = currData.filename + ` - ${tripId}`;
            this.updates$.next(currData);
          }
          if (status === 'ROSTER_DELETE' || status === 'DELETE') {
            const { dataType, data } = currData;
            if (dataType === 'DELETE_TRIP') {
              const { tripId: deleteTripId } = data;
              if (deleteTripId) {
                currData.filename = `Trip ${deleteTripId} is deleted.`;
                currData.statusMessage = 'Trip deleted successfully.';
                this.updates$.next(currData);
              }
            }
          }
          // this.createRoster(currData);
          break;
        }
        // case 'UNSUBSCRIBE': {
        //   this.remove(fileId);
        //   break;
        // }
      }
    }
  }

  startHeartbeat() {
    if (this.heartbeat$) {
      this.heartbeat$.unsubscribe();
    }
    this.heartbeat$ = interval(30000).subscribe(() => {
      this.sendWs('heartbeat');
    });
  }

  sendWs(action: 'subscribe' | 'unsubscribe' | 'heartbeat', fileId?: string) {
    if (this.socketWs$) {
      this.socketWs$.next({
        action,
        fileId,
      });
    }
  }

  loadCurrentProgress() {
    const localData = JSON.parse(localStorage.getItem(LOCALSTORAGE_KEY)) || [];
    if (localData?.length > 0) {
      this.initWs();
      this.uploadData.forEach((data: TimeTableProgressData) => {
        if (!data.completed) {
          this.sendWs('subscribe', data.id);
        }
        // if (!data.rosterCreated) {
        //   this.createRoster(data);
        // }
      });
      this.uploadData = localData;
    }
  }

  createRoster(data: TimeTableProgressData) {
    const { route, startDay, endDay, blockIds } = data;
    const arrRequests = blockIds?.map(blockId => {
      return this.rosterService.createroster({
        route_id: route,
        userid: blockId,
        block_id: +blockId,
        end_day: endDay,
        start_day: startDay,
        start_time: '00:00',
        end_time: '23:59',
      });
    });
    forkJoin(arrRequests).subscribe(resultArr => {
      console.log('Roster created: ', resultArr);
      data.rosterCreated = true;
      // localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(this.uploadData));
    });
  }

  cleanup() {
    this.clear();
    this.heartbeat$?.unsubscribe?.();
    this.heartbeat$ = undefined;
    this.socketWs$?.unsubscribe?.();
    this.socketWs$ = undefined;
  }
}
